import React from "react"
import { IRecord, ITargetsJSON } from "../types/schema"
import { HoverResourceLoader } from "./HoverResourceLoader"
import { buildRoute, eRoutes } from "../routes"
import { formatPrice, proRataCurrentTarget, proRataPctInt } from "../utils/utils"
import { KeyValueList } from "../Components/KeyValueList"
import { startOfMonth } from "date-fns"
import { getTargetForMonth } from "../../shared/dashboardData"

type ISalesRep = IRecord<"sales_reps", "name"> & {
  customerCount: number
  salesToDate: number
  targetsJSON: ITargetsJSON
}

interface IProps {
  id?: string
  // pass at least the name so the target can render without running the full
  // (customer count + sales this month) query, which then only runs on click
  initialData?: IRecord<"sales_reps", "name">
  renderInlineLink?: "block" | "inline"
  noLink?: boolean
  inline?: boolean
}

export function HoverSalesRepLoader({ id, initialData, renderInlineLink, noLink, inline }: IProps) {
  const skeem = window.skeem

  async function loadData() {
    const salesRep = await skeem.fetchOne<ISalesRep>({
      sales_reps: {
        attributes: [
          "name",
          "targetsJSON",
          { as: "customerCount", value: { count: { attr: "customers" } } },
          {
            as: "salesToDate",
            value: {
              sumPath: [
                {
                  name: "invoice_items",
                  filter: {
                    matches: { invoice_date: { gte: startOfMonth(new Date()) } },
                  },
                },
                { attr: "total_price_pennies" },
              ],
            },
          },
        ],
        filter: { matches: { id } },
      },
    })
    return salesRep
  }

  if (!id) {
    return <span>-</span>
  }

  return (
    <HoverResourceLoader<ISalesRep, IRecord<"sales_reps", "name">>
      reloadKey={id}
      inline={inline}
      loadData={loadData}
      initialData={initialData}
      resourceName="SalesRep"
      viewLink={
        noLink
          ? undefined
          : salesRep => buildRoute(eRoutes["/crm/sales-reps/:id"], { id: salesRep.id })
      }
      renderHoverContentTitle={record => record.name}
      renderInlineLink={renderInlineLink}
      renderHoverContent={record => (
        <KeyValueList
          data={[
            { label: "# Customers", value: record.customerCount },
            {
              label: "Target",
              value: formatPrice(getTargetForMonth("revenueTarget", record.targetsJSON) ?? 0, {
                isPennies: true,
                currency: "£",
              }),
            },
            {
              label: "Pro Rata Target",
              value: formatPrice(
                proRataCurrentTarget(getTargetForMonth("revenueTarget", record.targetsJSON) ?? 0),
                {
                  isPennies: true,
                  currency: "£",
                },
              ),
            },
            {
              label: "Sales this month",
              value: formatPrice(record.salesToDate, { currency: "£", isPennies: true }),
            },
            {
              label: "% Pro Rata",
              value: `${proRataPctInt(
                getTargetForMonth("revenueTarget", record.targetsJSON) ?? 0,
                record.salesToDate ?? 0,
              ).toFixed(2)}%`,
            },
          ]}
        />
      )}
      renderTarget={record => <span>{record.name}</span>}
    />
  )
}
